import { apiRequest } from '@/shared/lib/api-client';

export interface CampaignRow {
  id: number;
  name: string;
  status: 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'errored';
  template_id: number;
  template_name: string;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_count: number;
  pending: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  created_at: string;
}

export interface CampaignDetail {
  id: number;
  name: string;
  status: CampaignRow['status'];
  template_id: number;
  whatsapp_number_id: number;
  variables_map: Record<string, { kind: 'fixed'; value: string } | { kind: 'field'; path: string }>;
  header_media_id: number | null;
  /** Nome da mídia do cabeçalho (rótulo da galeria ou nome original). */
  header_media_name: string | null;
  message_delay_seconds: number;
  response_window_minutes: number;
  response_destination: string;
  response_chatbot_id: number | null;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_count: number;
  counts: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface RecipientRow {
  id: number;
  contact_id: number;
  contact_name: string | null;
  phone: string | null;
  message_id: number | null;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  error_code: string | null;
  error_message: string | null;
  attempted_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  failed_at: string | null;
}

export interface ListCampaignsResponse {
  campaigns: CampaignRow[];
  next_cursor: string | null;
  total: number;
}
export function listCampaigns(
  input: {
    q?: string | undefined;
    status?: CampaignRow['status'] | undefined;
    cursor?: string | null | undefined;
    limit?: number | undefined;
  } = {},
): Promise<ListCampaignsResponse> {
  const params = new URLSearchParams();
  if (input.q) params.set('q', input.q);
  if (input.status) params.set('status', input.status);
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.limit) params.set('limit', String(input.limit));
  const qs = params.toString();
  return apiRequest<ListCampaignsResponse>(`/campaigns${qs ? `?${qs}` : ''}`);
}
export function getCampaign(id: number): Promise<CampaignDetail> {
  return apiRequest<CampaignDetail>(`/campaigns/${id}`);
}

export type RecipientSortColumn =
  | 'contact'
  | 'status'
  | 'attempted'
  | 'delivered'
  | 'read'
  | 'error';

export interface ListRecipientsResponse {
  recipients: RecipientRow[];
  next_offset: number | null;
  total: number;
}
export function listRecipients(
  id: number,
  filters: {
    status?: RecipientRow['status'] | undefined;
    offset?: number | undefined;
    limit?: number | undefined;
    sort?: RecipientSortColumn | undefined;
    dir?: 'asc' | 'desc' | undefined;
  } = {},
): Promise<ListRecipientsResponse> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.offset) params.set('offset', String(filters.offset));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.sort) {
    params.set('sort', filters.sort);
    params.set('dir', filters.dir ?? 'asc');
  }
  const qs = params.toString();
  return apiRequest<ListRecipientsResponse>(`/campaigns/${id}/recipients${qs ? `?${qs}` : ''}`);
}

export interface VariableMappingFixed {
  kind: 'fixed';
  value: string;
}
export interface VariableMappingField {
  kind: 'field';
  path: string;
}
/** Variável alimentada por uma coluna da planilha de upload (valor por linha). */
export interface VariableMappingColumn {
  kind: 'column';
  column: string;
}
export type VariableMapping =
  | VariableMappingFixed
  | VariableMappingField
  | VariableMappingColumn;

/** Filtro de destinatários resolvido server-side ("selecionar todos os N").
 *  Snake_case porque vai direto no payload da API. */
export interface RecipientFilterPayload {
  q?: string;
  tag_ids?: number[];
  access_group_ids?: number[];
  last_inbound_from?: string;
  last_inbound_to?: string;
}

export interface CreateCampaignInput {
  name: string;
  template_id: number;
  whatsapp_number_id: number;
  variables_map: Record<string, VariableMapping>;
  contact_ids?: number[];
  tag_ids?: number[];
  access_group_ids?: number[];
  /** Lista de upload (CSV) — números crus. Backend separa quem já é contato
   *  (entra no fluxo normal, aparece na conversa) de quem é número novo
   *  (destinatário "cru"; contato nasce só na resposta). */
  upload_recipients?: Array<{
    phone: string;
    name?: string;
    /** Valores por linha das colunas mapeadas às variáveis (slot → valor). */
    variables?: Record<string, string>;
  }>;
  scheduled_at?: string | null;
  /** Mídia do header (template com header IMAGE/VIDEO/DOCUMENT). */
  header_media_id?: number | null;
  /** Seleção por filtro — backend resolve os destinatários (escala 30k+). */
  recipient_filter?: RecipientFilterPayload;
  /** Modo de envio: manual (cria parada) / now (dispara já) / scheduled (data). */
  send_mode?: 'manual' | 'now' | 'scheduled';
  /** Intervalo entre mensagens (segundos, 0-60). 0 = imediato. */
  message_delay_seconds?: number;
  /** Roteamento de resposta (etapa "Respostas"): janela + destino. */
  response_window_minutes?: number;
  response_destination?: 'channel_default' | 'chatbot';
  response_chatbot_id?: number | null;
}

export function createCampaign(
  input: CreateCampaignInput,
): Promise<{ id: number; status: string; total: number }> {
  return apiRequest('/campaigns', { method: 'POST', body: input });
}
export function pauseCampaign(id: number): Promise<void> {
  return apiRequest(`/campaigns/${id}/pause`, { method: 'POST', body: {} });
}
export function resumeCampaign(id: number): Promise<void> {
  return apiRequest(`/campaigns/${id}/resume`, { method: 'POST', body: {} });
}
export function cancelCampaign(id: number): Promise<void> {
  return apiRequest(`/campaigns/${id}/cancel`, { method: 'POST', body: {} });
}
export function deleteCampaign(id: number): Promise<void> {
  return apiRequest(`/campaigns/${id}`, { method: 'DELETE' });
}

// Templates listing — reuses settings endpoint
export interface TemplateRow {
  id: number;
  name: string;
  language: string;
  status: string;
  category: string;
  variableCount: number;
  components: unknown[];
  whatsappAccountId: number;
}
/** Template pelo id, em qualquer status — o da campanha pode ter sido pausado
 *  ou desabilitado depois do envio, e a prévia continua precisando dele. */
export function getTemplate(id: number): Promise<TemplateRow> {
  return apiRequest<TemplateRow>(`/templates/${id}`);
}
export function listApprovedTemplates(whatsappNumberId?: number): Promise<TemplateRow[]> {
  const qs = whatsappNumberId ? `?whatsapp_number_id=${whatsappNumberId}` : '';
  return apiRequest(`/templates${qs}`);
}

export interface ContactPickerRow {
  id: number;
  display_name: string | null;
  primary_identifier: { kind: string; value: string } | null;
}
export function searchContacts(
  q: string,
): Promise<{ contacts: ContactPickerRow[]; next_cursor: string | null }> {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('limit', '50');
  return apiRequest(`/contacts?${params.toString()}`);
}

/** Lista contatos do sistema com filtros pra seleção de campanha:
 *  nome (q), etiquetas (tagIds) e janela do último inbound (período). */
export interface CampaignContactFilters {
  q?: string;
  tagIds?: number[];
  accessGroupIds?: number[];
  lastInboundFrom?: string; // ISO
  lastInboundTo?: string; // ISO
  limit?: number;
  /** Paginação por offset (tabela com páginas numeradas). Quando presente, o
   *  backend usa OFFSET/LIMIT com ordem estável (id ASC) em vez de cursor. */
  offset?: number;
}
export function listCampaignContacts(
  f: CampaignContactFilters,
): Promise<{ contacts: ContactPickerRow[]; next_cursor: string | null; total: number }> {
  const params = new URLSearchParams();
  if (f.q) params.set('q', f.q);
  if (f.tagIds && f.tagIds.length > 0) params.set('tag_ids', f.tagIds.join(','));
  if (f.accessGroupIds && f.accessGroupIds.length > 0)
    params.set('access_group_ids', f.accessGroupIds.join(','));
  if (f.lastInboundFrom) params.set('last_inbound_from', f.lastInboundFrom);
  if (f.lastInboundTo) params.set('last_inbound_to', f.lastInboundTo);
  if (typeof f.offset === 'number') params.set('offset', String(f.offset));
  params.set('limit', String(f.limit ?? 200));
  return apiRequest(`/contacts?${params.toString()}`);
}

/** Linha da lista de upload (CSV) de uma campanha — número cru. Materializada
 *  no backend só na finalização (não grava no upload). */
export interface CampaignImportRow {
  display_name: string;
  phone: string;
  email?: string;
  /** Valores das colunas de variável (parametro_N) desta linha, keyados pelo
   *  número do slot ({{N}}). Presente só quando a planilha tem colunas de
   *  variável. Vazio ('') quando a coluna existe mas a célula está em branco. */
  variables?: Record<string, string>;
}

// ─── Rascunhos (progresso parcial do wizard) ─────────────────────────────
/** Estado de visualização do passo Destinatários — persistido pra reabrir o
 *  rascunho exatamente na tela que o usuário deixou (modo + filtros + busca). */
export interface RecipientsView {
  mode: 'system' | 'upload' | null;
  dateFrom: string;
  dateTo: string;
  filterTagIds: number[];
  filterAccessGroupIds: number[];
  nameQuery: string;
  applied: {
    q?: string;
    tagIds: number[];
    accessGroupIds: number[];
    from?: string;
    to?: string;
  } | null;
}

/** Seleção por filtro guardada no wizard: a regra + a contagem (pra exibir
 *  "N selecionados"). Não guarda os contatos — o backend resolve no disparo. */
export interface RecipientFilterSelection {
  q?: string;
  tagIds: number[];
  accessGroupIds: number[];
  from?: string;
  to?: string;
  count: number;
}

/** Estado serializado do wizard salvo num rascunho. Todos opcionais — o
 *  rascunho pode estar incompleto. */
export interface CampaignDraftState {
  step?: number;
  name?: string;
  whatsappNumberId?: number | null;
  templateId?: number | null;
  variables?: Record<string, VariableMapping>;
  headerMediaId?: number | null;
  /** Nome da mídia do cabeçalho — só pra prévia (cartão do documento). */
  headerMediaName?: string | null;
  selectedContacts?: ContactPickerRow[];
  filterSelection?: RecipientFilterSelection | null;
  /** Lista de upload (CSV) — números crus, persistida no rascunho. */
  uploadRows?: CampaignImportRow[];
  scheduledAt?: string;
  /** Modo de envio escolhido na etapa final. */
  sendMode?: 'manual' | 'now' | 'scheduled';
  /** Intervalo entre mensagens (segundos, 0 = imediato) — throttle da campanha. */
  messageDelaySeconds?: number;
  /** Etapa "Respostas": janela (minutos) + destino do roteamento da resposta. */
  responseWindowMinutes?: number;
  responseDestination?: 'channel_default' | 'chatbot';
  responseChatbotId?: number | null;
  recipientsView?: RecipientsView;
}
export interface CampaignDraftRow {
  id: number;
  name: string;
  updated_at: string;
}
export function listCampaignDrafts(): Promise<CampaignDraftRow[]> {
  return apiRequest('/campaign-drafts');
}
export function getCampaignDraft(
  id: number,
): Promise<{ id: number; name: string; state: CampaignDraftState; updated_at: string }> {
  return apiRequest(`/campaign-drafts/${id}`);
}
export function createCampaignDraft(
  name: string,
  state: CampaignDraftState,
): Promise<{ id: number }> {
  return apiRequest('/campaign-drafts', { method: 'POST', body: { name, state } });
}
export function updateCampaignDraft(
  id: number,
  name: string,
  state: CampaignDraftState,
): Promise<{ ok: true }> {
  return apiRequest(`/campaign-drafts/${id}`, { method: 'PUT', body: { name, state } });
}
export function deleteCampaignDraft(id: number): Promise<{ ok: true }> {
  return apiRequest(`/campaign-drafts/${id}`, { method: 'DELETE' });
}

export interface TagOption {
  id: number;
  name: string;
  color: string;
  scope: string;
}
export function listContactTags(): Promise<TagOption[]> {
  return apiRequest('/tags?scope=contact');
}
